import type { VariantOptionsFe } from "@intelligems/ig-types";
import {
  AudienceFilterDto,
  CreateAudienceDto,
  ExperienceFe,
  ExperienceType,
  ExpressionNodeType,
  OfferDto,
  OfferProductDto,
  QuantityButtonsState,
  QuantityButtonsUserInterface,
  RedirectDto,
  VariationFe,
} from "@intelligems/ig-types";
import {
  renderConditionText as computeConditionText,
  getQuantityButtonsVariantForOptions,
  hasStringDuplicates,
  renderAudienceActionText,
} from "@intelligems/ig-utils";

type ProductVariantOptions = {
  productId: string;
  options: VariantOptionsFe[];
};
import { ProductPickerState } from "@src/store/slices/productPicker/types";

import { getSpecificRedirectsUtil } from "../components/redirects/getSpecificRedirects";

export const getDomainValue = (input: string, domain: string) => {
  let value: string;
  if (input === "") {
    value = input;
  } else if (domain.length === 0) {
    value = input;
  } else {
    try {
      const url = new URL(input);
      value = url.pathname.substring(1);
    } catch (e) {
      value = "";
    }
  }

  if (domain.length === 0) {
    return value;
  }

  value = value.split(`${domain}/`).pop() || "";
  value = value.includes(domain) ? "" : value;
  if (value.startsWith("/")) {
    value = value.substring(1);
  }
  return value;
};

export const shippingRateValidator = (variations: VariationFe[]) => {
  if (variations) {
    const isCustomRate = variations.some((group) =>
      group.shippingRateGroups!.some(
        (rateGroup) => rateGroup.groupType === "custom"
      )
    );
    if (isCustomRate) {
      return {
        isDisabled: false,
        reason: null,
      };
    } else {
      const hasEmptyShippingRates = variations?.some(
        (group) => group.shippingRateGroups!.length < 1
      );
      return {
        isDisabled: hasEmptyShippingRates,
        reason: hasEmptyShippingRates
          ? "Create rates for each test group"
          : null,
      };
    }
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

/**
 * Whether a shipping experience still needs configuration before it can run.
 *
 * A shipping test is configured once the merchant has chosen which Shopify
 * rates it replaces and a source for the new rates exists. For the Intelligems
 * carrier that source is the per-group manual rates; for a custom carrier the
 * rates come from the published custom provider, so the manual-rate validation
 * does not apply.
 */
export const shippingExperienceNotConfigured = ({
  status,
  type,
  shippingTestMethodDefinitions,
  variations,
  isCustomCarrier,
  hasActiveGlobalProvider = false,
}: {
  status: ExperienceFe["status"];
  type: ExperienceFe["type"];
  shippingTestMethodDefinitions: ExperienceFe["shippingTestMethodDefinitions"];
  variations: VariationFe[];
  isCustomCarrier: boolean;
  hasActiveGlobalProvider?: boolean;
}): boolean => {
  if (status === "ended" || type !== "shipping") {
    return false;
  }
  if ((shippingTestMethodDefinitions || []).length <= 0) {
    return true;
  }
  if (hasActiveGlobalProvider && !isCustomCarrier) {
    return true;
  }
  if (isCustomCarrier) {
    return false;
  }
  return shippingRateValidator(variations).isDisabled;
};

export const renderConditionText = (expression: ExpressionNodeType[]) =>
  computeConditionText(expression);

export const renderTargetExperimentBehavior = (
  variations: VariationFe[],
  queryParam: AudienceFilterDto,
  entity: "experiment" | "campaign"
) =>
  renderAudienceActionText(queryParam.action, {
    variationId: queryParam.variationId,
    excludeFromAnalytics: queryParam.excludeFromAnalytics,
    variations,
    entity,
  });

export const urlRedirectValidator = (
  contentFlowType: ExperienceType,
  specificRedirectErrors: string[],
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  if (specificRedirectErrors.length > 0) {
    return {
      isDisabled: true,
      reason: "Fix all redirect errors before continuing",
    };
  }

  const urlRedirects = getSpecificRedirectsUtil(redirectsState, "url");
  if (contentFlowType === "content/url" || urlRedirects.length) {
    if (urlRedirects.length === 0) {
      return { isDisabled: true, reason: "Create URL redirects" };
    }
    if (
      urlRedirects.some(
        (redirect) =>
          redirect.queryParams.length > 0 &&
          redirect.queryParams.some(
            (queryParam) => !queryParam.key || queryParam.key.length === 0
          )
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect query parameters",
      };
    }

    if (
      urlRedirects.some(
        (redirect) =>
          redirect.originUrl.length === 0 ||
          (!redirect.destinationUrl && !redirect.skip) ||
          (redirect.destinationUrl &&
            redirect.destinationUrl.length === 0 &&
            !redirect.skip) ||
          (redirect.redirectType === "partialReplacement" &&
            !redirect.skip &&
            (!redirect.find || redirect.find.length === 0))
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect URL inputs",
      };
    }
  }

  return { isDisabled: false, reason: null };
};

export const templateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const templateRedirects = redirectsState.filter(
    (r) => r.isTemplateTest && !r.skip
  );
  if (templateRedirects.length) {
    for (const templateRedirect of templateRedirects) {
      if (!templateRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a template for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/template") {
    const hasNoTemplateRedirects =
      getSpecificRedirectsUtil(redirectsState, "template").length === 0;
    return {
      isDisabled: hasNoTemplateRedirects,
      reason: hasNoTemplateRedirects
        ? "Select a template for each group"
        : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const themeRedirects = redirectsState.filter((r) => r.isThemeTest && !r.skip);
  if (themeRedirects.length) {
    for (const themeRedirect of themeRedirects) {
      if (!themeRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a theme for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/theme") {
    const hasNoThemeRedirects =
      getSpecificRedirectsUtil(redirectsState, "theme").length === 0;
    return {
      isDisabled: hasNoThemeRedirects,
      reason: hasNoThemeRedirects ? "Select a theme for each group" : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeOrTemplateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const isThemeNotValid = themeValidator(contentFlowType, redirectsState);
  const isTemplateNotValid = templateValidator(contentFlowType, redirectsState);
  if (isThemeNotValid.isDisabled) {
    return isThemeNotValid;
  }
  if (isTemplateNotValid.isDisabled) {
    return isTemplateNotValid;
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const variationDetailsValidator = (
  variations: VariationFe[]
): { isDisabled: boolean; reason: string | null } => {
  if ((variations || []).length <= 1) {
    return {
      isDisabled: true,
      reason: "Create at least two test groups",
    };
  }
  if (hasStringDuplicates((variations || []).map((tg) => tg.name))) {
    return {
      isDisabled: true,
      reason: "Provide a unique name for each test group",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const productPickerValidator = (
  productPickerState: ProductPickerState,
  id: string | undefined
) => {
  if (id) {
    return {
      isDisabled: false,
      reason: null,
    };
  } else {
    const noProductsSelected = productPickerState.selectedProducts.length === 0;

    return {
      isDisabled: noProductsSelected,
      reason: noProductsSelected ? "Select at least one product" : null,
    };
  }
};

export const googleAnalyticsValidator = (
  isGoogleAnalyticsNextDisabled: boolean
) => {
  return {
    isDisabled: isGoogleAnalyticsNextDisabled,
    reason: isGoogleAnalyticsNextDisabled
      ? "Allow Google Analytics to load before continuing"
      : null,
  };
};

export const isAudienceTargetingEnabled = (audience: CreateAudienceDto) => {
  return (audience?.filters?.length ?? 0) > 0;
};

export const offerValidator = (
  offers: OfferDto[],
  variations?: VariationFe[]
): { isDisabled: boolean; reason: string | null } => {
  if (offers.length === 0) {
    return { isDisabled: false, reason: null };
  }

  const offersToValidate = variations ? offers : [offers[0]];

  for (const offer of offersToValidate) {
    if (!offer.name || offer.name.trim().length === 0) {
      return {
        isDisabled: true,
        reason: "Offer name is required",
      };
    }

    switch (offer.discountApplicationType) {
      case "cartDiscount":
        const firstTier = offer.tiers[0];
        if (!firstTier || firstTier.unitDiscount === 0) {
          return {
            isDisabled: true,
            reason: "Discount value cannot be 0",
          };
        }
        break;

      case "buyXGetY": {
        // Mirrors the server-side refinement in ig-types/dtos/offer.refinements
        // so the merchant gets the reason here rather than a save failure.
        const bxgyTier = offer.tiers[0];
        if (
          !bxgyTier ||
          !Number.isInteger(bxgyTier.minimumUnits) ||
          bxgyTier.minimumUnits < 1
        ) {
          return {
            isDisabled: true,
            reason: "Buy quantity must be a whole number of at least 1",
          };
        }
        if (
          !Number.isInteger(bxgyTier.bxgyGetQuantity) ||
          bxgyTier.bxgyGetQuantity < 1
        ) {
          return {
            isDisabled: true,
            reason: "Get quantity must be a whole number of at least 1",
          };
        }
        if (bxgyTier.unitDiscount <= 0) {
          return { isDisabled: true, reason: "Discount value cannot be 0" };
        }
        if (
          offer.discountType === "percentage" &&
          bxgyTier.unitDiscount > 100
        ) {
          return {
            isDisabled: true,
            reason: "A percentage discount cannot exceed 100%",
          };
        }
        break;
      }

      case "tieredDiscount":
        if (offer.tiers.length < 2) {
          return {
            isDisabled: true,
            reason: "At least 2 tiers are required for volume discounts",
          };
        }

        if (offer.tiers.some((t) => t.isFreeShipping)) {
          if (
            offer.shippingDiscount &&
            offer.shippingDiscount.rateFilter !== null &&
            offer.shippingDiscount.rateFilter.type === "name"
          ) {
            if (offer.shippingDiscount.rateFilter.rules.length === 0) {
              return {
                isDisabled: true,
                reason: `Shipping rate conditions are required`,
              };
            }
            for (
              let i = 0;
              i < offer.shippingDiscount.rateFilter.rules.length;
              i++
            ) {
              if (offer.shippingDiscount.rateFilter.rules[i].name === "") {
                return {
                  isDisabled: true,
                  reason: `Shipping rate name cannot be empty`,
                };
              }
            }
          }
        }
        if (
          offer.tiers.length !==
          new Set(offer.tiers.map((t) => t.minimumUnits)).size
        ) {
          return {
            isDisabled: true,
            reason: `Each tier must have a unique ${offer.unitType === "unit" ? "item quantity" : "subtotal"}`,
          };
        }
        for (let i = 0; i < offer.tiers.length; i++) {
          const tier = offer.tiers[i];
          if (i !== 0 && tier.minimumUnits <= 0) {
            return {
              isDisabled: true,
              reason: `Offer tier ${i + 1} requires a minimum purchase ${offer.unitType === "unit" ? "quantity" : "subtotal"}`,
            };
          }
          if (tier.unitDiscount < 0) {
            return {
              isDisabled: true,
              reason: `Tier ${i + 1} requires a discount value`,
            };
          }
        }
        break;

      case "freeShipping":
        if (
          offer.shippingDiscount &&
          offer.shippingDiscount.rateFilter !== null &&
          offer.shippingDiscount.rateFilter.type === "name"
        ) {
          if (offer.shippingDiscount.rateFilter.rules.length === 0) {
            return {
              isDisabled: true,
              reason: `Shipping rate conditions are required`,
            };
          }
          for (
            let i = 0;
            i < offer.shippingDiscount.rateFilter.rules.length;
            i++
          ) {
            if (offer.shippingDiscount.rateFilter.rules[i].name === "") {
              return {
                isDisabled: true,
                reason: `Shipping rate name cannot be empty`,
              };
            }
          }
        }
        break;
      case "freeGift":
        const hasGiftProduct = offer.tiers.some(
          (tier) => tier.isGiftWithPurchase && tier.giftWithPurchaseProductId
        );

        if (!hasGiftProduct) {
          return {
            isDisabled: true,
            reason: "A free gift product must be selected",
          };
        }
        break;
    }
  }

  return { isDisabled: false, reason: null };
};

export const quantityButtonValidator = (
  quantityButtons: QuantityButtonsUserInterface,
  offerProducts?: OfferProductDto[],
  productVariantOptions?: ProductVariantOptions[]
) => {
  if (
    quantityButtons.widget?.config.desktop?.variant ===
    "quantityButtonsWidgetImage"
  ) {
    const buttons = quantityButtons.desktopVariables?.buttons || [];
    if (buttons.length !== buttons.filter((button) => !!button.image).length) {
      return {
        isDisabled: true,
        reason: "Each button must have an image",
      };
    }
  }

  const variantSettings = quantityButtons.desktopVariables?.variantSettings;
  const buttons = quantityButtons.desktopVariables?.buttons;

  if (
    variantSettings?.enabled &&
    buttons &&
    offerProducts &&
    offerProducts.length > 0
  ) {
    for (const product of offerProducts) {
      const productSettings = variantSettings.products?.find(
        (p) => p.productId === product.id
      );

      if (!productSettings?.defaultVariants) continue;

      const includedVariants = product.variants.filter(
        (v) => v.inclusionType === "include"
      );

      if (includedVariants.length === 0) {
        return {
          isDisabled: true,
          reason: `No eligible variants for ${product.title}. Add variants to this offer first.`,
        };
      }

      if (!productVariantOptions) continue;

      const variantOptions = productVariantOptions.find(
        (vo) => vo.productId === product.id
      );
      if (!variantOptions) continue;

      const variantOptionsMap = variantOptions.options.reduce(
        (acc, v) => {
          acc[v.variantId] = {
            options: v.options,
            availableForSale: v.availableForSale,
          };
          return acc;
        },
        {} as Record<
          string,
          { options: Record<string, string>; availableForSale: boolean }
        >
      );

      const variants = includedVariants.map((v) => {
        const variantData = variantOptionsMap[v.id];
        return {
          id: v.id,
          availableForSale: variantData?.availableForSale ?? false,
          options: variantData?.options ?? {},
        };
      });

      const stateOptions = Object.entries(
        productSettings.allOptionValues ?? {}
      ).map(([name, values]) => ({
        name,
        values: values.map((v) => ({ id: "", value: v })),
      }));

      const state: QuantityButtonsState = {
        options: stateOptions,
        variants,
        buttons: [],
        quantity: 0,
      };

      const maxQuantity = Math.max(...buttons.map((b) => b.quantity));
      for (let variantIndex = 0; variantIndex < maxQuantity; variantIndex++) {
        const defaultOptions = productSettings.defaultVariants?.[variantIndex];

        if (!defaultOptions || Object.keys(defaultOptions).length === 0) {
          return {
            isDisabled: true,
            reason: `Default variant ${variantIndex + 1} for ${product.title} is not configured`,
          };
        }

        const variant = getQuantityButtonsVariantForOptions(
          state,
          defaultOptions
        );

        if (!variant) {
          return {
            isDisabled: true,
            reason: `Default variant ${variantIndex + 1} for ${product.title} is not eligible for this offer`,
          };
        }

        if (!variant.availableForSale) {
          return {
            isDisabled: true,
            reason: `Default variant ${variantIndex + 1} for ${product.title} is sold out`,
          };
        }
      }
    }
  }

  return {
    isDisabled: false,
    reason: null,
  };
};
